import SparkMD5 from 'spark-md5';

/**
 * Calculate MD5 checksum for a file
 * @param {File} file - The file to calculate checksum for
 * @returns {Promise<string>} Base64 encoded MD5 checksum
 */
async function calculateMD5(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const spark = new SparkMD5.ArrayBuffer();

    reader.onload = (e) => {
      spark.append(e.target.result);
      const hexHash = spark.end();

      // Convert hex to base64
      const bytes = new Uint8Array(
        hexHash.match(/.{1,2}/g).map((byte) => parseInt(byte, 16)),
      );
      const base64 = btoa(String.fromCharCode.apply(null, bytes));
      resolve(base64);
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Upload file directly to Wasabi via Rails Active Storage
 * @param {File} file - The file to upload
 * @param {string} authToken - JWT authentication token
 * @returns {Promise<string>} signed_id for attaching to models
 */
const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
export async function uploadToWasabi(file, authToken) {
  try {
    // Step 1: Calculate MD5 checksum
    const checksum = await calculateMD5(file);

    // Step 2: Request presigned URL from Rails
    const directUploadResponse = await fetch(
      `${baseUrl}/rails/active_storage/direct_uploads`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          blob: {
            filename: file.name,
            content_type: file.type,
            byte_size: file.size,
            checksum: checksum,
          },
        }),
      },
    );

    if (!directUploadResponse.ok) {
      throw new Error('Failed to get presigned URL');
    }

    const {
      direct_upload: { url, headers },
      signed_id,
    } = await directUploadResponse.json();

    // Step 3: Upload directly to Wasabi
    const uploadResponse = await fetch(url, {
      method: 'PUT',
      headers: headers,
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload to Wasabi');
    }

    // Step 4: Return signed_id to attach to model
    return signed_id;
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
}

/**
 * Upload multiple files
 * @param {File[]} files - Array of files to upload
 * @param {string} authToken - JWT authentication token
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<string[]>} Array of signed_ids
 */
export async function uploadMultipleToWasabi(
  files,
  authToken,
  onProgress = null,
) {
  const results = [];

  for (let i = 0; i < files.length; i++) {
    const signedId = await uploadToWasabi(files[i], authToken);
    results.push(signedId);

    if (onProgress) {
      onProgress(((i + 1) / files.length) * 100);
    }
  }

  return results;
}
